import { ItemsViewLayout } from '@app/components/items-view-layout-switch/ItemsViewLayoutSwitch';
import { StationStatusListItem } from '@app/modules/logger/screens/status/types';
import { Box, Grid } from '@mui/material';
import { useState } from 'react';
import { StatusViewProvider } from '../../../context/StatusViewContext';
import { useStatusSections } from '../../../hooks/useStatusSections';
import { NoStationsFoundMessage } from '../../NoStationsFoundMessage';
import { SectionHeader } from './SectionHeader';
import { StationGridItems } from './StationGridItems';

interface StatusLayoutProps {
  statuses: StationStatusListItem[];
  variant?: ItemsViewLayout;
}

/**
 * Layout for favorites/others sections and empty state.
 * Renders station items in a grid (gridWide / gridNormal / gridCompact) or list (full-width rows) via StationGridItems.
 */
export function StatusLayout({ statuses, variant = 'gridNormal' }: StatusLayoutProps) {
  const [itemWidth, setItemWidth] = useState(0);

  const {
    favorites,
    nonFavorites,
    visibleFavorites,
    visibleNonFavorites,
    showSections,
    hasVisibleStations,
  } = useStatusSections(statuses);

  const itemsContent = (items: StationStatusListItem[]) => (
    <Grid container spacing={2} mb={5}>
      <StationGridItems items={items} variant={variant} onItemWidthChange={setItemWidth} />
    </Grid>
  );

  const content = !hasVisibleStations ? (
    <NoStationsFoundMessage />
  ) : showSections ? (
    <>
      {visibleFavorites.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionHeader
            id="logger.statusScreen.favoritesSection"
            defaultMessage="Favoritas ({count})"
            count={visibleFavorites.length}
          />
          {itemsContent(favorites)}
        </Box>
      )}
      {visibleNonFavorites.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionHeader
            id="logger.statusScreen.othersSection"
            defaultMessage="Otras ({count})"
            count={visibleNonFavorites.length}
          />
          {itemsContent(nonFavorites)}
        </Box>
      )}
    </>
  ) : (
    itemsContent(nonFavorites)
  );

  return (
    <StatusViewProvider compact={variant !== 'list'} itemWidth={itemWidth}>
      {content}
    </StatusViewProvider>
  );
}
